/* eslint-disable @typescript-eslint/ban-types */
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { combineLatest, Observable, of, Subject, throwError } from 'rxjs';
import { Profile } from '../../models/profile';
import { environment } from '../../../environments/environment';
import { catchError, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { ApiUrl } from '../../../app/constants/ApiUrls';
import { SnackbarType } from '../../constants/Enums';
import { SnackBarService } from '../services/snackbar.service';
import { TranslateService } from '@ngx-translate/core';
import { ChangePassword } from '../../models/changePassword';

@Injectable()
export class ProfileApiService {
  private readonly onDestroy$ = new Subject<void>();
  constructor(private readonly _http: HttpClient, private readonly _snackbarService: SnackBarService, private readonly _translateService: TranslateService) {}

  public getProfile(id: string): Observable<Profile> {
    return this._http
      .get(`${ApiUrl.profiles}/${id}`)
      .pipe(map(data => data as Profile));
  }

  public createProfile(profile: Profile): Observable<Profile> {
    return this._http
      .post(ApiUrl.profiles, profile)
      .pipe(map(data => data as Profile));
  }

  public saveProfile(id: string, profile: Profile): Observable<Object> {
    return this._http.put(`${ApiUrl.profiles}/${id}`, profile).pipe(
      tap(() => {
        let success = '';
        let successMsg = '';
        this._translateService
          .get('EDIT_PROFILE.success')
          .pipe(takeUntil(this.onDestroy$))
          .subscribe((value) => (success = value));
        this._translateService
          .get('EDIT_PROFILE.save_success')
          .pipe(takeUntil(this.onDestroy$))
          .subscribe((value) => (successMsg = value));
        this._snackbarService.showSnackbar(
          SnackbarType.success,
          success,
          successMsg
        );
      }),
      catchError((error) => {
        this._handleError();
        return throwError(() => error);
      })
    );
  }

  public getChangePasswordTicket(): Observable<string> {
    return this._http
      .post<ChangePassword>(`${ApiUrl.profiles}/change-password`, {})
      .pipe(
        map(response => response.ticket),
        catchError((error) => {
          return combineLatest([
            this._translateService.get('ERRORS.error'),
            this._translateService.get('ERRORS.retry')
          ]).pipe(
            takeUntil(this.onDestroy$),
            tap(([errorTitle, genericErrorMsg]) => {
              this._snackbarService.showSnackbar(SnackbarType.error, errorTitle, genericErrorMsg);
            }),
            switchMap(() => throwError(() => error))
          );
        })
      );
  }

  public deleteUser(id: string): Observable<boolean> {
    return this._http.delete(`${ApiUrl.profiles}/${id}`).pipe(
      switchMap(() => of(true)),
      catchError(() => {
        return combineLatest([
          this._translateService.get('ERRORS.error'),
          this._translateService.get('ERRORS.retry')
        ]).pipe(
          takeUntil(this.onDestroy$),
          tap(([errorTitle, genericErrorMsg]) => {
            this._snackbarService.showSnackbar(SnackbarType.error, errorTitle, genericErrorMsg);
          }),
          switchMap(() => of(false))
        );
      })
    );
  }

  private _handleError() {
    let errorTitle ='';
    let genericErrorMsg = "";
    this._translateService
    .get('EDIT_PROFILE.save_unsuccess')
    .pipe(takeUntil(this.onDestroy$))
    .subscribe((value) => (genericErrorMsg = value));
    this._translateService
      .get('EDIT_PROFILE.unsuccessful')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((value) => (errorTitle = value));
    this._snackbarService.showSnackbar(SnackbarType.error, errorTitle, genericErrorMsg);
  }

  public deleteProfile(): Observable<boolean> {
    return this._http
      .patch(`${environment.baseAPIUrl}/Users/DeleteSchedule`, {})
      .pipe(map(data => data as boolean));
  }

  public cancelDeleteProfile(): Observable<boolean> {
    return this._http
      .delete(`${environment.baseAPIUrl}/Users/DeleteSchedule`, {})
      .pipe(map(data => data as boolean));
  }

  public tosAccept(id: string, profile: Profile): Observable<boolean> {
    const patchObj: Object = { countryCode: profile.country, isToSAccepted: true };
    return this._http
      .patch(`${environment.baseAPIUrl}/Users/${id}`, patchObj)
      .pipe(
        map(res => true as boolean)
      );
  }

  public consentAccept(): Observable<boolean> {
    return this._http
      .post(`${environment.baseAPIUrl}/Consent`, {})
      .pipe(
        map(res => true as boolean)
      );
  }

  public consentRevoke(): Observable<boolean> {
    return this._http
      .delete(`${environment.baseAPIUrl}/Consent`)
      .pipe(
        map(res => true as boolean)
      );
  }

}
